import type { AsyncSendState } from "./AsyncSendState";
import type { ComposeOperation } from "./ComposeOperation";
import type { FromViewState } from "./FromViewState";
import type { OwaDate } from "owa-datetime";
import type { AddinViewState } from "owa-editor-addin-plugin-types/lib/utils/AddinViewState";
import type { AttachmentWellViewState } from "owa-attachment-well-data";
import type DrawingCanvasElementsViewState from "owa-editor-graphic-plugin-types/lib/store/schema/DrawingCanvasElementsViewState";
import type DropViewState from "owa-dnd/lib/store/schema/DropViewState";
import type { CompositeEditorViewState } from "owa-editor/lib/store/schema/CompositeEditorViewState";
import type ComposeRecipientWellViewState from "./ComposeRecipientWellViewState";
import type { InfoBarHostViewState } from "owa-info-bar/lib/schema/InfoBarMessageViewState";
import type ProtectionViewState from "owa-mail-protection-types/lib/schema/ProtectionViewState";
import type PolicyTipsViewState from "owa-policy-tips/lib/store/schema/PolicyTipsViewState";
import type ImportanceType from "owa-service/lib/contract/ImportanceType";
import type MeetingRequestMessageType from "owa-service/lib/contract/MeetingRequestMessageType";
import type SensitivityType from "owa-service/lib/contract/SensitivityType";
import type SmimeViewState from "owa-smime-types/lib/schema/SmimeViewState";
import type ComposePluginViewState from "./ComposePluginViewState";
import type { UIEnabledState } from "./UIEnabledState";
import type { SmartPillViewState } from "owa-mail-smart-pill-features-types";
import type AccCheckerViewState from "owa-editor-acc-checker/lib/store/schema/AccCheckerViewState";
import type ContentPickerViewState from "owa-editor-content-picker/lib/store/schema/ContentPickerViewState";
import type PostOpenTask from "./PostOpenTask";
import type { AugloopSession } from "augloop-service";
import type LightningViewState from "./LightningViewState";
import type { ClientItemId } from "owa-client-ids";
import type { MailboxInfo } from "owa-client-types";
import type { PerformanceDatapoint } from "owa-analytics";
import type { ComposeControl } from "./ComposeControl";
import type CopilotComposeViewState from "owa-copilot-drafts-utils/lib/types/CopilotComposeViewState";
import type CopilotSubjectViewState from "owa-copilot-suggested-subject/lib/types/CopilotSubjectViewState";
import type DLPAnnotationViewState from "owa-editor-augloopdlp-utils/lib/store/schema/DLPAnnotationViewState";
import type { SideStripViewState } from "owa-compose-side-strip-skittles/lib/types/SideStripViewState";
import type { BizChatHandoffData } from "./BizChatHandoffData";
import type { MailMergeViewState } from "./MailMergeViewState";

export const DRAFT_SAVE_FAILED = false;

/**
 * The latest draft save state:
 * - `null`: The draft has never been saved.
 * - `DRAFT_SAVE_FAILED`: The latest relevant save attempt failed.
 * - `string`: The server-provided timestamp of the latest successful save.
 */
export type DraftSaveStatus = string | null | typeof DRAFT_SAVE_FAILED;

export interface ComposeViewState extends ComposePluginViewState, CompositeEditorViewState, InfoBarHostViewState, ComposeRecipientWellViewState {
    // mailboxInfo that contains account/group/public folder information
    mailboxInfo: MailboxInfo;
    
    // Message properties
    operation: ComposeOperation;
    itemId: ClientItemId | null;
    subject: string;
    referenceItemId: ClientItemId | null;
    referenceItemDocumentId: number;
    attachmentWell: AttachmentWellViewState;
    importance: ImportanceType;
    fromViewState: FromViewState;
    policyTipsViewState: PolicyTipsViewState;
    meetingRequestItem: Partial<MeetingRequestMessageType> | undefined;
    isReadReceiptRequested: boolean;
    isDeliveryReceiptRequested: boolean;
    copilotInboxScore: number | undefined /* The importance of the mail item being referred, derived from referenceItem */;
    
    // Currently only used for storing BeautifulLink Images. In the future, it will need to be modified to store all sharingLinkIds.
    linksContainerId: string;
    
    // UI properties
    isInlineCompose: boolean;
    conversationId: string | undefined;
    lastSaveTimeStamp: DraftSaveStatus;
    isRibbonShown: boolean;
    isSending: boolean;
    isSavingSpinnerShown: boolean;
    isMailboxChangeInProgress?: boolean;
    composeFormKey?: string;
    showCompactRecipientWell: boolean;
    dropViewState: DropViewState;
    showSendDiscardAsIcon: boolean;
    uiEnabledState: UIEnabledState;
    accChecker: AccCheckerViewState;
    contentPicker: ContentPickerViewState;
    isDialogOpen: boolean;
    isComposeRendered: boolean;
    isComposeOutbox?: boolean;
    autoFocusControl?: ComposeControl;
    isFileActivation?: boolean;
    
    //S/MIME  properties
    smimeViewState: SmimeViewState;
    smimeQuotedBody?: string;
    
    // Add-in decryption quoted body
    addinDecryptionQuotedBody?: string;
    
    // UpConvert / Upgrade
    quotedBody?: string;
    // Quoted body that can be sent to server side
    // src attribute is removed from inline images
    quotedBodyForSend?: string;
    // attachments waiting for item id, i.e. before upgrade or in new message scenarios
    numberOfWaitingAttachments: number;
    
    // Async send
    preferAsyncSend: boolean;
    asyncSendState: AsyncSendState;
    handledByDelaySend: boolean;
    
    // Internal data, for multiple compose instance
    composeId: string;
    expressionId: string; // ID for expression pane instance
    changeAsyncSendStateTimer: number;
    
    // Used for logging only. Needed so we can correlate between opening and closing compose
    logTraceId: string;
    
    // Message sensitivity setting
    sensitivity: SensitivityType;
    
    // Protection View State;
    protectionViewState: ProtectionViewState;
    
    // Flag that tracks if the draft was manually saved at any point.
    isManuallySaved: boolean;
    
    // Smart Pills
    smartPillViewState: SmartPillViewState;
    
    // Options property to viewstate for side strip
    sideStripViewState: SideStripViewState;
    
    // Scheduled Send Date
    deferredSendTime: OwaDate | null;
    
    // Skips the standard Undo Send bar for composes that render their own undo
    // affordance (e.g. a MyOutlook briefing draft reply), avoiding a collision.
    suppressUndoSendNotificationBar?: boolean;
    
    // Post open tasks
    postOpenTasks: PostOpenTask[];
    
    // Addins view state
    addin: AddinViewState;
    
    // Augloop session View State
    augloopSession: AugloopSession;
    
    // DLP Annotation View State
    dlpAnnotationViewState: DLPAnnotationViewState;
    
    // Lightning view state
    lightningViewState?: LightningViewState;
    
    suggestedAttachmentSessionId: string;
    
    // OPX config properties
    // Hide the send button in mail composer
    hideSend?: boolean;
    
    // Hide the discard button in mail composer
    hideDiscard?: boolean;
    
    // Hide the popout button in mail composer
    hidePopout?: boolean;
    
    // Hide the draft saved time in mail composer
    hideDraftSavedTime?: boolean;
    
    // Post message to OPX host when draft is saved in composer
    postMessageToHostOnDraftSaved?: boolean;
    
    // Hide the bottom of the mail composer
    hideComposeBottom?: boolean;
    
    inlineImageOverrideToBase64?: boolean;
    
    copilotViewState: CopilotComposeViewState;
    
    copilotSubjectViewState: CopilotSubjectViewState;
    
    // performance telemetry
    perfDatapoint?: PerformanceDatapoint;
    sendPerfDatapoint?: PerformanceDatapoint;
    
    drawingCanvasElementsViewState?: DrawingCanvasElementsViewState;
    
    // Disallow reactions for the current message
    shouldDisallowReactions: boolean;
    
    referenceAttachmentBlob?: string;
    
    // Indicates whether compose is created from an item, e.g. referenceItem or template item
    isCreatedFromItem?: boolean;
    
    // offline compose properties
    quotedBodyMissing?: boolean; // indicate that quoted body is required but has not been loaded into UI yet
    // Indicate the state where we want to clean up all pending save/send actions in Action Queue and do save/send from scratch using createItem
    shouldRefreshSaveInActionQueue?: boolean;
    
    mailMergeViewState: MailMergeViewState;
    isPrint?: boolean; // This is set when printing active drafts
    
    // When set to true, compose is ready to insert quoted body.
    // Otherwise, it means there are still other jobs to run before inserting quoted body, e.g. insertSignature
    readyToUpgrade?: boolean;
    
    // Remote Execute
    // To fix the XRF access issue, we set RemoteExecute for archive and shared folder scenarios.
    // For CreateItem, we do not set X-AnchorMailbox header, the request will always hit primary mailbox user's mailbox server.
    // On server side code, we have code fix to check the flag when reply/replyall/forward emails.
    remoteExecute: boolean;
    
    // The template name, only used for template which is in the template folder
    templateName?: string;
    
    isInAuthErrorState: boolean; // Indicate whether compose is in auth error state, e.g. user needs to re-authenticate
    
    bizChatHandoffData?: BizChatHandoffData;
}
